import type { TFunction } from 'i18next';
import type { PricingBreakdown, PricingResponse, SkippedCarrier } from '@carrierbridge/shared';
import { AlertTriangle, Search, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { formatEUR } from '@/lib/utils';
import { QuoteResultCard } from './QuoteResultCard';

/** Right-hand results column: loading / error / empty / no-carrier / ranked results. */
export function QuoteResultsPanel({
  t,
  isPending,
  response,
  results,
  skippedCarriers,
  bestPrice,
  unitLabel,
  isSimplifiedView,
  onOpenBreakdown,
  error,
  onRetry,
  onEditCriteria,
  sourceLabelOf,
}: {
  t: TFunction;
  isPending: boolean;
  response: PricingResponse | null;
  results: PricingBreakdown[];
  skippedCarriers: SkippedCarrier[];
  bestPrice: number | null;
  unitLabel: string;
  isSimplifiedView: boolean;
  onOpenBreakdown: (breakdown: PricingBreakdown) => void;
  error?: string | null;
  onRetry?: () => void;
  onEditCriteria?: () => void;
  /** Société propriétaire d'une grille quand ce n'est pas la nôtre (cotation de groupe), sinon rien. */
  sourceLabelOf?: (result: PricingBreakdown) => string | undefined;
}) {
  if (isPending) {
    return (
      <div className="space-y-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-20 mx-auto max-w-md rounded border border-danger/30 bg-surface p-5 shadow-card">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-danger" />
          <div className="min-w-0 flex-1">
            <h3 className="text-[13px] font-semibold text-text">{t('quote.pricingError')}</h3>
            <p className="mt-1 text-[13px] text-subtle leading-relaxed break-words">{error}</p>
            {onRetry && (
              <div className="mt-4">
                <Button variant="outline" size="sm" onClick={onRetry}>
                  {t('quote.retry')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!response) {
    return (
      <EmptyState
        icon={<TrendingUp className="h-6 w-6" />}
        title={t('quote.emptyTitle')}
        description={t('quote.emptyDescription')}
        className="mt-20"
      />
    );
  }

  if (results.length === 0) {
    return (
      <>
        <EmptyState
          icon={<Search className="h-6 w-6" />}
          title={t('quote.noCarrierTitle')}
          description={t('quote.noCarrierDescription')}
          action={onEditCriteria ? {
            label: t('quote.editCriteria'),
            onClick: onEditCriteria,
            variant: 'outline',
          } : undefined}
          className="mt-20 pb-6"
        />
        <div className="mx-auto max-w-xl">
          <SkippedCarriersList t={t} skippedCarriers={skippedCarriers} defaultOpen />
        </div>
      </>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center gap-4 rounded bg-surface border border-border/60 px-4 py-2.5 shadow-card">
        <span className="text-[13px] text-muted">
          {t('quote.carrier', { count: results.length })}
        </span>
        {bestPrice != null && (
          <>
            <span className="h-4 w-px bg-border" />
            <span className="text-[13px] text-muted">
              {t('quote.bestPrice')}{' '}
              <span className="font-semibold text-success">{formatEUR(bestPrice)}</span>
            </span>
          </>
        )}
      </div>

      {results
        .slice()
        .sort((a, b) => a.totalPrice - b.totalPrice)
        .map((result) => {
          const isBest = bestPrice != null && result.totalPrice === bestPrice;
          const diff = bestPrice != null ? result.totalPrice - bestPrice : 0;
          return (
            <QuoteResultCard
              key={`${result.carrierId}-${result.versionId}`}
              result={result}
              isBest={isBest}
              diff={diff}
              unitLabel={unitLabel}
              onClick={() => { if (!isSimplifiedView) onOpenBreakdown(result); }}
              simplified={isSimplifiedView}
              sourceLabel={sourceLabelOf?.(result)}
            />
          );
        })}

      <SkippedCarriersList t={t} skippedCarriers={skippedCarriers} />
    </div>
  );
}

/**
 * Transporteurs écartés, avec le motif que le moteur a donné (pas de grille à
 * cette date, pas de lane, pas de tranche pour la quantité…). Replié quand il y
 * a des résultats ; déplié quand il n'y en a aucun, car c'est alors la seule
 * explication de l'écran vide.
 */
function SkippedCarriersList({
  t,
  skippedCarriers,
  defaultOpen = false,
}: {
  t: TFunction;
  skippedCarriers: SkippedCarrier[];
  defaultOpen?: boolean;
}) {
  if (skippedCarriers.length === 0) return null;
  return (
    <details
      open={defaultOpen}
      className="rounded border border-warning/40 bg-warning-soft/50 overflow-hidden text-left"
    >
      <summary className="cursor-pointer px-4 py-2.5 text-xs font-medium text-warning">
        {t('quote.excludedCarriers', { count: skippedCarriers.length })}
      </summary>
      <div className="border-t border-warning/30 px-4 py-3 space-y-2.5">
        {skippedCarriers.map((sc, i) => (
          <div key={i} className="text-xs">
            <p className="font-medium text-text">
              {sc.carrierName || sc.carrierCode}
              {sc.gridLabel && (
                <span className="ml-1.5 font-normal text-subtle">{sc.gridLabel}</span>
              )}
            </p>
            {sc.reason && <p className="mt-0.5 text-muted">{sc.reason}</p>}
          </div>
        ))}
      </div>
    </details>
  );
}
